// React
import React from 'react'
import { createRoot } from 'react-dom/client'

// Libraries
import { StyleSheetManager } from 'styled-components'
import camelCase from 'lodash/camelCase'

// Shared
import { shouldForwardProp } from 'shared/utils/shouldForwardProp'

const registerCustomElement = (
  name,
  Component,
  { attributes, mountElement = 'div', mountAttributes = {}, styleSheetTarget } = {}
) => {
  const webComponentClass = class extends HTMLElement {
    constructor () {
      super()

      this.mountPoint = document.createElement(mountElement)
      this.mountPoint.className = name

      for (const [key, value] of Object.entries(mountAttributes)) {
        this.mountPoint.setAttribute(key, value)
      }

      this.attachShadow({ mode: 'open' })
    }

    connectedCallback () {
      if (this.isConnected) {
        this.setAttribute('initialized', 'true')

        const attrs = attributes?.reduce(
          (acc, key) =>
            Object.assign(acc, {
              [camelCase(key.replace('data-', ''))]:
                this.getAttribute(key) ?? undefined
            }),
          {}
        ) ?? {}

        const handleSetAttribute = (name, value) => {
          if (value === null) {
            this.removeAttribute(name)
          } else {
            this.setAttribute(name, value)
          }
        }

        this.shadowRoot?.appendChild(this.mountPoint)

        const root = createRoot(this.mountPoint)

        root.render(
          <StyleSheetManager
            target={styleSheetTarget || this.shadowRoot || undefined}
            shouldForwardProp={shouldForwardProp}
            enableVendorPrefixes
          >
            <Component {...attrs} setAttribute={handleSetAttribute}>{this.childNodes}</Component>
          </StyleSheetManager>
        )
      }
    }

    disconnectedCallback () {
      if (!this.isConnected) {
        this.shadowRoot?.removeChild(this.mountPoint)
      }
    }
  }

  if (!customElements.get(name)) {
    customElements.define(name, webComponentClass)
  }
}

export default registerCustomElement
